import React, { useEffect, useRef, useState } from "react";

import { useAppShell } from "../components/layout/AppShell";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { DEFAULT_INSTRUCTIONS, DEFAULT_TEMPLATE } from "../defaultTemplate";
import { djangoApi } from "../services/djangoApi";

const DEFAULT_TEMPLATE_TEXT = JSON.stringify(DEFAULT_TEMPLATE, null, 2);
const POLL_MS = 3000;
const UPLOAD_ATTEMPTS = 4;
const OPEN_STATUSES = new Set(["uploading", "pending", "processing"]);
const FINISHED_STATUSES = new Set(["completed", "failed"]);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function errorMessage(e, fallback) {
  const data = e?.response?.data;
  return data?.error || data?.detail || e?.message || fallback;
}

// Green only for a clean read; a completed job with an error message returned
// the template defaults, so it counts as an error.
function StatusIndicator({ status, error }) {
  if (OPEN_STATUSES.has(status)) {
    return (
      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    );
  }
  if (status === "completed" && !error) {
    return <span className="font-bold text-green-600">&#10003;</span>;
  }
  return <span className="font-bold text-red-600">&#10007;</span>;
}

function downloadJson(task) {
  const blob = new Blob([JSON.stringify(task.result, null, 2)], {
    type: "application/json",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${task.fileName.replace(/\.pdf$/i, "")}.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 0);
}

export default function NewExtractionPage({ apiKey }) {
  const { selectedOrgId } = useAppShell();
  const [instructions, setInstructions] = useState(DEFAULT_INSTRUCTIONS);
  const [jsonTemplateText, setJsonTemplateText] = useState(DEFAULT_TEMPLATE_TEXT);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [tasks, setTasks] = useState([]);
  const [openTaskId, setOpenTaskId] = useState(null);
  const fileInputRef = useRef(null);
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  useEffect(() => {
    if (!apiKey) return;
    let isMounted = true;
    djangoApi
      .listJsonTemplates(apiKey, selectedOrgId, 100)
      .then((data) => {
        if (isMounted) setTemplates(Array.isArray(data?.results) ? data.results : []);
      })
      .catch(() => {
        // Saved templates are optional; the default one always works.
      });
    return () => {
      isMounted = false;
    };
  }, [apiKey, selectedOrgId]);

  const updateTask = (id, patch) =>
    setTasks((all) => all.map((task) => (task.id === id ? { ...task, ...patch } : task)));

  const hasOpenTasks = tasks.some((task) => task.ulid && OPEN_STATUSES.has(task.status));

  // One list request per tick for every status; the full result is fetched once,
  // when a document finishes. Keeps the request rate flat however many PDFs.
  useEffect(() => {
    if (!hasOpenTasks || !apiKey) return undefined;
    let busy = false;
    const timer = setInterval(async () => {
      if (busy) return;
      busy = true;
      try {
        const list = await djangoApi.listProcessed(apiKey, selectedOrgId, 100);
        const statusByUlid = new Map((list?.results || []).map((item) => [item.ulid, item.status]));
        const open = tasksRef.current.filter((task) => task.ulid && OPEN_STATUSES.has(task.status));
        for (const task of open) {
          const status = statusByUlid.get(task.ulid);
          if (!FINISHED_STATUSES.has(status)) {
            if (status && status !== task.status) updateTask(task.id, { status });
            continue;
          }
          const detail = await djangoApi.getProcessedDetail(apiKey, selectedOrgId, task.ulid);
          updateTask(task.id, {
            status: detail.status,
            result: detail.json_result,
            error: detail.error_message,
            seconds: detail.duration_s,
          });
        }
      } catch {
        // Transient polling errors: retried on the next tick.
      } finally {
        busy = false;
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [hasOpenTasks, apiKey, selectedOrgId]);

  // Rate limited or briefly unavailable: wait and try again instead of failing.
  async function uploadWithRetry(formData) {
    for (let attempt = 1; ; attempt += 1) {
      try {
        return await djangoApi.processPdfAsyncVector(apiKey, selectedOrgId, formData);
      } catch (e) {
        const status = e?.response?.status;
        if (attempt >= UPLOAD_ATTEMPTS || (status !== 429 && status !== 503)) throw e;
        const retryAfter = Number(e.response.headers?.["retry-after"]) || 2 ** attempt;
        await sleep(retryAfter * 1000);
      }
    }
  }

  async function processFiles(fileList) {
    const pdfs = Array.from(fileList).filter(
      (file) => file.type === "application/pdf" || /\.pdf$/i.test(file.name),
    );
    if (!pdfs.length) return;

    let jsonTemplate;
    try {
      jsonTemplate = JSON.parse(jsonTemplateText);
    } catch {
      setError("JSON template must be valid JSON.");
      return;
    }
    setError("");

    const added = pdfs.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      fileName: file.name,
      status: "uploading",
    }));
    setTasks((all) => [...all, ...added.map(({ file, ...task }) => task)]);

    // One task per file, uploaded in order, so each document gets its own JSON.
    for (const { id, file } of added) {
      try {
        const formData = new FormData();
        formData.append("pdf", file);
        formData.append("instructions", instructions);
        formData.append("json_template", JSON.stringify(jsonTemplate));
        const response = await uploadWithRetry(formData);
        updateTask(id, { ulid: response.ulid, status: "pending" });
      } catch (e) {
        updateTask(id, { status: "failed", error: errorMessage(e, "Upload failed") });
      }
    }
  }

  const completedCount = tasks.filter((task) => task.status === "completed").length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Process Document</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <button
            type="button"
            onClick={() => fileInputRef.current.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              processFiles(e.dataTransfer.files);
            }}
            className={[
              "w-full rounded-lg border-2 border-dashed py-16 text-sm transition-colors",
              dragging
                ? "border-primary bg-primary/5 text-primary"
                : "border-border text-muted-foreground hover:border-primary/50",
            ].join(" ")}
          >
            Drop PDFs here or click to choose
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            multiple
            hidden
            onChange={(e) => {
              processFiles(e.target.files);
              e.target.value = "";
            }}
          />

          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          <details className="space-y-4">
            <summary className="cursor-pointer select-none text-sm text-muted-foreground">
              Template
            </summary>
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="templateSelect">Template Preset</Label>
                <Select
                  id="templateSelect"
                  value={selectedTemplateId}
                  onChange={(e) => {
                    const templateId = e.target.value;
                    setSelectedTemplateId(templateId);
                    const selected = templates.find((item) => item.ulid === templateId);
                    setInstructions(selected ? selected.instructions || "" : DEFAULT_INSTRUCTIONS);
                    setJsonTemplateText(
                      selected
                        ? JSON.stringify(selected.json_template || {}, null, 2)
                        : DEFAULT_TEMPLATE_TEXT,
                    );
                  }}
                >
                  <option value="">Default</option>
                  {templates.map((template) => (
                    <option key={template.ulid} value={template.ulid}>
                      {template.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="instructions">Instructions</Label>
                <Textarea
                  id="instructions"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  className="min-h-[200px] font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jsonTemplate">JSON Structure</Label>
                <Textarea
                  id="jsonTemplate"
                  value={jsonTemplateText}
                  onChange={(e) => setJsonTemplateText(e.target.value)}
                  className="min-h-[220px] font-mono"
                />
              </div>
            </div>
          </details>
        </CardContent>
      </Card>

      {tasks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Documents</CardTitle>
            <CardDescription>
              {completedCount} / {tasks.length} completed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {tasks.map((task) => (
                <div key={task.id} className="rounded-lg border p-3 text-sm">
                  <div className="flex items-center gap-3">
                    <StatusIndicator status={task.status} error={task.error} />
                    <div className="min-w-0 flex-1 truncate font-medium">{task.fileName}</div>
                    <div className="text-xs text-muted-foreground">
                      {task.status === "completed"
                        ? `${(task.seconds || 0).toFixed(1)} s`
                        : task.status}
                    </div>
                    {task.status === "completed" ? (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setOpenTaskId(openTaskId === task.id ? null : task.id)}
                        >
                          {openTaskId === task.id ? "Hide" : "View JSON"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => downloadJson(task)}
                        >
                          Download
                        </Button>
                      </>
                    ) : null}
                  </div>
                  {task.error ? (
                    <div className="mt-2 text-xs text-red-600">
                      {task.error}
                    </div>
                  ) : null}
                  {openTaskId === task.id ? (
                    <pre className="mt-3 max-h-[480px] overflow-auto rounded-md border bg-muted p-3 text-xs">
                      {JSON.stringify(task.result, null, 2)}
                    </pre>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="mt-3">
              <Button type="button" variant="outline" size="sm" onClick={() => setTasks([])}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
